import React, { useEffect, useMemo, useState } from 'react'
import { portal } from '../portalApi.js'
import { fmt } from '../api.js'

// 분류 규칙 — 조건에 맞는 거래에 계정과목·세목·부서·거래처를 붙인다.
//
// 예전에는 분류를 붙이는 길이 셋으로 흩어져 있었다(부서 규칙·카드 부서·계정과목 학습).
// "이 거래가 왜 이렇게 분류됐지"를 알려면 세 군데를 뒤져야 했다. 여기 모았다.
//
// 붙이는 순서가 있다. 뒤가 앞을 덮지 않는다.
//   1 사람이 고친 것   무엇도 덮지 않는다
//   2 규칙            구체적인 조건이 먼저다
//   3 카드 부서        카드 한 장에 정해 둔 부서. 가장 성긴 1차 분류다
//
// 소급 적용은 규칙이 붙였던 것만 다시 계산한다. 사람 손과 빈칸은 건드리지 않는다.

const 빈규칙 = {
  name: '', kinds: '', cond_account: '', cond_card: '', cond_vendor: '', cond_keyword: '',
  cond_min: '', cond_max: '', cond_direction: '',
  set_category_id: '', set_category_name: '', set_subitem_id: '', set_subitem_name: '',
  set_dept_code: '', set_dept_name: '', set_vendor_name: '',
  priority: 100, active: 1, memo: '',
}

const 출처말 = { manual: '사람', rule: '규칙', card: '카드', '(빈칸)': '미지정' }

function 조건요약(r) {
  const t = []
  if (r.cond_card) t.push(`카드 ${r.cond_card}`)
  if (r.cond_account) t.push(`계좌 ${r.cond_account}`)
  if (r.cond_vendor) t.push(`거래처 "${r.cond_vendor}"`)
  if (r.cond_keyword) t.push(`적요에 "${r.cond_keyword}"`)
  if (r.cond_direction) t.push(r.cond_direction === 'IN' ? '입금' : '출금')
  if (r.cond_min != null && r.cond_min !== '') t.push(`${fmt(r.cond_min)}원 이상`)
  if (r.cond_max != null && r.cond_max !== '') t.push(`${fmt(r.cond_max)}원 이하`)
  return t.join(' · ') || '(조건 없음 — 걸리지 않습니다)'
}

function 라벨요약(r) {
  const t = []
  if (r.set_category_name) t.push(r.set_subitem_name ? `${r.set_category_name} · ${r.set_subitem_name}` : r.set_category_name)
  if (r.set_dept_name) t.push(r.set_dept_name)
  if (r.set_vendor_name) t.push(`거래처 ${r.set_vendor_name}`)
  return t.join(' / ') || '—'
}

function RuleForm({ initial, data, onSave, onCancel }) {
  const [f, setF] = useState({ ...빈규칙, ...(initial || {}) })
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }))
  const 계정 = data.accounts.find((a) => a.code === f.set_category_id)

  return (
    <div className="panel" style={{ padding: 16, marginBottom: 12 }}>
      <div className="row" style={{ marginBottom: 10 }}>
        <input placeholder="규칙 이름 (예: 카카오T 택시 → 여비교통비)" style={{ flex: 1, minWidth: 220 }}
          value={f.name || ''} onChange={(e) => set('name', e.target.value)} />
        <label className="muted" style={{ fontSize: 12 }}>우선순위
          <input type="number" style={{ width: 70, marginLeft: 4 }} value={f.priority ?? 100}
            onChange={(e) => set('priority', Number(e.target.value))} />
        </label>
        <span className="muted" style={{ fontSize: 11 }}>낮은 수가 먼저 걸립니다</span>
      </div>

      <div className="hint" style={{ margin: '0 0 8px' }}>조건 — 넣은 것끼리 <b>모두</b> 맞아야 걸립니다</div>
      <div className="row" style={{ marginBottom: 10, flexWrap: 'wrap' }}>
        <select value={f.kinds || ''} onChange={(e) => set('kinds', e.target.value)} style={{ width: 110 }}>
          <option value="">모든 자료</option>
          <option value="bank">계좌</option>
          <option value="card">신용카드</option>
          <option value="tax">세금계산서</option>
        </select>
        <input placeholder="적요에 들어가는 말" style={{ width: 180 }}
          value={f.cond_keyword || ''} onChange={(e) => set('cond_keyword', e.target.value)} />
        <input placeholder="거래처(정확히 일치)" style={{ width: 170 }}
          value={f.cond_vendor || ''} onChange={(e) => set('cond_vendor', e.target.value)} />
        <input placeholder="카드번호" style={{ width: 150 }}
          value={f.cond_card || ''} onChange={(e) => set('cond_card', e.target.value)} />
        <input placeholder="계좌번호" style={{ width: 140 }}
          value={f.cond_account || ''} onChange={(e) => set('cond_account', e.target.value)} />
        <select value={f.cond_direction || ''} onChange={(e) => set('cond_direction', e.target.value)}
          style={{ width: 90 }}>
          <option value="">입출금 무관</option>
          <option value="IN">입금만</option>
          <option value="OUT">출금만</option>
        </select>
        <input placeholder="금액 이상" inputMode="numeric" style={{ width: 110 }}
          value={f.cond_min ?? ''} onChange={(e) => set('cond_min', e.target.value)} />
        <input placeholder="금액 이하" inputMode="numeric" style={{ width: 110 }}
          value={f.cond_max ?? ''} onChange={(e) => set('cond_max', e.target.value)} />
      </div>

      <div className="hint" style={{ margin: '0 0 8px' }}>붙일 것 — 고른 것만 붙습니다</div>
      <div className="row" style={{ marginBottom: 10, flexWrap: 'wrap' }}>
        <select value={f.set_category_id || ''} style={{ width: 200 }}
          onChange={(e) => {
            const a = data.accounts.find((x) => x.code === e.target.value)
            setF((s) => ({ ...s, set_category_id: e.target.value, set_category_name: a ? a.name : '',
              set_subitem_id: '', set_subitem_name: '' }))
          }}>
          <option value="">계정과목 그대로</option>
          {data.accounts.map((a) => (
            <option key={a.code} value={a.code}>{a.name} ({a.code})</option>
          ))}
        </select>
        {계정 && 계정.subitems.length > 0 && (
          <select value={f.set_subitem_id || ''} style={{ width: 170 }}
            onChange={(e) => {
              const s2 = 계정.subitems.find((x) => String(x.id) === e.target.value)
              setF((s) => ({ ...s, set_subitem_id: e.target.value, set_subitem_name: s2 ? s2.name : '' }))
            }}>
            <option value="">세목 없이</option>
            {계정.subitems.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
          </select>
        )}
        <select value={f.set_dept_code || ''} style={{ width: 170 }}
          onChange={(e) => {
            const d = data.departments.find((x) => x.code === e.target.value)
            setF((s) => ({ ...s, set_dept_code: e.target.value, set_dept_name: d ? d.name : '' }))
          }}>
          <option value="">부서 그대로</option>
          {data.departments.map((d) => <option key={d.code} value={d.code}>{d.name}</option>)}
        </select>
        <input placeholder="거래처 이름" style={{ width: 160 }}
          value={f.set_vendor_name || ''} onChange={(e) => set('set_vendor_name', e.target.value)} />
      </div>

      <div className="row">
        <label className="muted" style={{ fontSize: 12, display: 'inline-flex', gap: 5 }}>
          <input type="checkbox" checked={!!f.active} onChange={(e) => set('active', e.target.checked ? 1 : 0)} />
          쓰는 중
        </label>
        <input placeholder="메모" style={{ flex: 1, minWidth: 160 }}
          value={f.memo || ''} onChange={(e) => set('memo', e.target.value)} />
        <button className="btn" onClick={() => onSave(f)}>저장</button>
        <button className="btn secondary" onClick={onCancel}>취소</button>
      </div>
    </div>
  )
}

export default function LabelRules() {
  const [data, setData] = useState(null)
  const [err, setErr] = useState('')
  const [editing, setEditing] = useState(null)
  const [적용중, set적용중] = useState(false)
  const [toast, setToast] = useState('')

  const notify = (m) => { setToast(m); setTimeout(() => setToast(''), 3200) }

  const load = async () => {
    setErr('')
    try { setData(await portal.get('/api/meta/rules')) }
    catch (e) { setErr(e.message) }
  }
  useEffect(() => { load() }, [])

  const save = async (f) => {
    try {
      await portal.post('/api/meta/rules', f)
      setEditing(null); notify('저장했습니다'); load()
    } catch (e) { setErr(e.message) }
  }

  const remove = async (r) => {
    if (!window.confirm(`"${r.name || '이름 없는 규칙'}" 을 지울까요?`)) return
    try { await portal.del(`/api/meta/rules/${r.id}`); load() }
    catch (e) { setErr(e.message) }
  }

  const 적용 = async (소급) => {
    if (소급 && !window.confirm(
      '규칙이 붙였던 분류를 다시 계산합니다.\n사람이 직접 고친 것은 그대로 둡니다.\n계속할까요?')) return
    set적용중(true)
    try {
      const r = await portal.post('/api/meta/rules/apply', { start: '2026-07-01', retro: !!소급 })
      notify(`규칙 ${r.규칙}개로 ${fmt(r.본것)}건을 훑어 계정과목 ${fmt(r.계정)} · 부서 ${fmt(r.부서)} · 거래처 ${fmt(r.거래처)}건을 붙였습니다`)
      load()
    } catch (e) { setErr(e.message) } finally { set적용중(false) }
  }

  const 통계 = useMemo(() => {
    if (!data) return []
    const 이름 = { bank: '계좌', card: '신용카드', tax: '세금계산서' }
    return Object.entries(data.stats).map(([k, v]) => ({ kind: 이름[k] || k, ...v }))
  }, [data])

  return (
    <>
      <div className="row" style={{ marginBottom: 12, flexWrap: 'wrap' }}>
        <button className="btn" onClick={() => setEditing({})}>+ 규칙 추가</button>
        <button className="btn secondary" disabled={적용중} onClick={() => 적용(false)}>
          {적용중 ? '붙이는 중…' : '빈칸 채우기'}
        </button>
        <button className="btn secondary" disabled={적용중} onClick={() => 적용(true)}>
          과거까지 다시 붙이기
        </button>
        <span className="muted" style={{ marginLeft: 'auto', fontSize: 12 }}>
          규칙 {data ? data.rules.length : 0}개
        </span>
      </div>

      <div className="hint" style={{ marginBottom: 10 }}>
        붙이는 순서가 있습니다 — <b>사람이 고친 것 &gt; 규칙 &gt; 카드 부서</b>. 뒤가 앞을 덮지 않습니다.
        <br />
        카드에 정해 둔 부서는 <b>1차 분류</b>입니다. A팀 카드라도 실제로는 B팀이 쓴 지출일 수 있으니,
        데이터 화면에서 거래별로 고치면 그게 이깁니다. <b>과거까지 다시 붙이기</b>를 눌러도 사람이 고친 것은 그대로 둡니다.
      </div>

      {err && <div className="error-box">{err}</div>}
      {editing && data && (
        <RuleForm initial={editing.id ? editing : null} data={data}
          onSave={save} onCancel={() => setEditing(null)} />
      )}

      {data && (
        <>
          <div className="panel" style={{ marginBottom: 12 }}>
            <table>
              <thead>
                <tr>
                  <th style={{ width: 46 }}>순서</th>
                  <th className="l" style={{ width: 200 }}>이름</th>
                  <th className="l">조건</th>
                  <th className="l" style={{ width: 220 }}>붙일 것</th>
                  <th style={{ width: 60 }}>걸림</th>
                  <th style={{ width: 110 }} />
                </tr>
              </thead>
              <tbody>
                {data.rules.length === 0 && (
                  <tr><td colSpan={6} className="c muted">규칙이 없습니다. 「+ 규칙 추가」로 만드세요.</td></tr>
                )}
                {data.rules.map((r) => (
                  <tr key={r.id} style={r.active ? undefined : { opacity: .5 }}>
                    <td className="c muted">{r.priority}</td>
                    <td className="l">
                      {r.name || <span className="muted">(이름 없음)</span>}
                      {!r.active && <span className="muted" style={{ fontSize: 11 }}> · 꺼둠</span>}
                      {r.memo && <div className="muted" style={{ fontSize: 11 }}>{r.memo}</div>}
                    </td>
                    <td className="l muted" style={{ fontSize: 12 }}>{조건요약(r)}</td>
                    <td className="l" style={{ fontSize: 13 }}>{라벨요약(r)}</td>
                    <td className="c muted">{r.hits ? fmt(r.hits) : '—'}</td>
                    <td className="c" style={{ whiteSpace: 'nowrap' }}>
                      <button className="btn secondary" style={{ padding: '3px 8px', fontSize: 11 }}
                        onClick={() => setEditing(r)}>수정</button>{' '}
                      <button className="btn danger" style={{ padding: '3px 8px', fontSize: 11 }}
                        onClick={() => remove(r)}>삭제</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="panel">
            <table>
              <thead>
                <tr>
                  <th className="l" style={{ width: 110 }}>자료</th>
                  <th className="l">계정과목을 무엇이 붙였나 (7월~)</th>
                  <th className="l">부서를 무엇이 붙였나</th>
                </tr>
              </thead>
              <tbody>
                {통계.map((s) => (
                  <tr key={s.kind}>
                    <td className="l">{s.kind}</td>
                    {['계정과목', '부서'].map((축) => (
                      <td key={축} className="l muted" style={{ fontSize: 12 }}>
                        {Object.entries(s[축] || {})
                          .sort((a, b) => b[1] - a[1])
                          .map(([k, v]) => `${출처말[k] || k} ${fmt(v)}`).join(' · ') || '—'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {toast && <div className="toast">{toast}</div>}
    </>
  )
}
